import React from "react";

import { STATES_INVERTED } from "Constants";
import {
  composeValidators,
  parseNonNegativeNumber,
  validateDate,
  validateHundred,
  validateName,
  validateNumber,
  validatePhone,
  validatePhysicalAddress,
  validateRequired,
  validateSSN,
  validateZero,
  validateZipCode,
} from "Utils/Validations";

import { SIFinalFormRadioGroupNoCol, SIFormSelect } from "Common/forms";
import { dateMask, phoneMask, ssnMask, zipCodeMask } from "Common/forms/masks";
import { SIFinalFormFieldNoCol } from "Common/forms/SIFinalFormField";
import { Input as ThemeInput } from "Theme/Input";

interface BeneficiaryFieldProps {
  inputName?: string;
  inputNamePrefix?: string;
  componentProps?: Record<string, any>;
  required?: boolean;
  [rest: string]: any;
}
export const Beneficiary1Name = ({ inputName = "beneficiary1Name", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Name"
    component={ThemeInput}
    validate={composeValidators(validateRequired, validateName)}
    {...rest}
  />
);

export const Beneficiary1Relationship = ({ inputName = "beneficiary1Relationship", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Relationship"
    component={ThemeInput}
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary1Percentage = ({ inputName = "beneficiary1Percentage", inputNamePrefix, componentProps = {}, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Percentage"
    component={ThemeInput}
    parse={(v) => {
      if (v === "") return "1";
      return v;
    }}
    componentProps={{
      appendContent: "%",
      mask: [/[1-9]/, /\d/],
      ...componentProps,
    }}
    validate={composeValidators(validateRequired, validateZero, validateHundred, validateNumber)}
    {...rest}
  />
);

export const Beneficiary1BirthDate = ({ inputName = "beneficiary1BirthDate", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Birth Date"
    component={ThemeInput}
    componentProps={{ placeholder: "MM/DD/YYYY", mask: dateMask }}
    validate={composeValidators(validateRequired, validateDate)}
    {...rest}
  />
);

export const Beneficiary1SocialSecurityNumber = ({
  inputName = "beneficiary1SocialSecurityNumber",
  inputNamePrefix,
  required = true,
  ...rest
}: BeneficiaryFieldProps) => {
  const validations = required ? composeValidators(validateRequired, validateSSN) : validateSSN;
  return (
    <SIFinalFormFieldNoCol
      name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
      label="Social Security Number"
      component={ThemeInput}
      componentProps={{ mask: ssnMask }}
      validate={validations}
      {...rest}
    />
  );
};

export const Beneficiary1PhoneNumber = ({ inputName = "beneficiary1PhoneNumber", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Phone Number"
    component={ThemeInput}
    componentProps={{ placeholder: "(555) 555-5555", mask: phoneMask }}
    validate={composeValidators(validateRequired, validatePhone)}
    {...rest}
  />
);

export const Beneficiary1Address = ({ inputName = "beneficiary1Address", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Address"
    component={ThemeInput}
    validate={composeValidators(validateRequired, validatePhysicalAddress)}
    {...rest}
  />
);

export const Beneficiary1City = ({ inputName = "beneficiary1City", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="City"
    component={ThemeInput}
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary1State = ({ inputName = "beneficiary1State", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="State"
    component={SIFormSelect}
    placeholder="-----"
    componentProps={{ options: STATES_INVERTED }}
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary1ZipCode = ({ inputName = "beneficiary1ZipCode", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Zip Code"
    component={ThemeInput}
    componentProps={{ mask: zipCodeMask }}
    validate={composeValidators(validateZipCode, validateRequired)}
    {...rest}
  />
);

export const Beneficiary2Type = ({ inputName = "beneficiary2Type", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormRadioGroupNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    options={{
      none: "No second beneficiary - Primary receives the entire benefit",
      primary: "Primary - Two beneficiaries split the benefit",
      contingent: "Contingent - Contingent beneficiary only receives the benefit if the primary is deceased",
    }}
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary2Name = ({ inputName = "beneficiary2Name", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Name"
    component={ThemeInput}
    validate={composeValidators(validateRequired, validateName)}
    {...rest}
  />
);

export const Beneficiary2Relationship = ({ inputName = "beneficiary2Relationship", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    component={ThemeInput}
    label="Relationship"
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary2Percentage = ({ inputName = "beneficiary2Percentage", inputNamePrefix, componentProps, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Percentage"
    component={ThemeInput}
    componentProps={{
      appendContent: "%",
      type: "number",
      min: 0,
      max: 100,
      ...componentProps,
    }}
    parse={parseNonNegativeNumber}
    validate={composeValidators(validateRequired, validateZero, validateHundred, validateNumber)}
    {...rest}
  />
);

export const Beneficiary2BirthDate = ({ inputName = "beneficiary2BirthDate", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Birth Date"
    component={ThemeInput}
    type="text"
    componentProps={{ placeholder: "MM/DD/YYYY", mask: dateMask }}
    validate={composeValidators(validateRequired, validateDate)}
    {...rest}
  />
);

export const Beneficiary2SocialSecurityNumber = ({
  inputName = "beneficiary2SocialSecurityNumber",
  inputNamePrefix,
  required = true,
  ...rest
}: BeneficiaryFieldProps) => {
  const validations = required ? composeValidators(validateRequired, validateSSN) : validateSSN;
  return (
    <SIFinalFormFieldNoCol
      name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
      label="Social Security Number"
      component={ThemeInput}
      componentProps={{ mask: ssnMask }}
      validate={validations}
      {...rest}
    />
  );
};

export const Beneficiary2PhoneNumber = ({ inputName = "beneficiary2PhoneNumber", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Phone Number"
    component={ThemeInput}
    componentProps={{ placeholder: "(555) 555-5555", mask: phoneMask }}
    validate={composeValidators(validateRequired, validatePhone)}
    {...rest}
  />
);

export const Beneficiary2Address = ({ inputName = "beneficiary2Address", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Address"
    component={ThemeInput}
    validate={composeValidators(validateRequired, validatePhysicalAddress)}
    {...rest}
  />
);

export const Beneficiary2City = ({ inputName = "beneficiary2City", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="City"
    component={ThemeInput}
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary2State = ({ inputName = "beneficiary2State", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="State"
    component={SIFormSelect}
    placeholder="-----"
    componentProps={{ options: STATES_INVERTED }}
    validate={validateRequired}
    {...rest}
  />
);

export const Beneficiary2ZipCode = ({ inputName = "beneficiary2ZipCode", inputNamePrefix, ...rest }: BeneficiaryFieldProps) => (
  <SIFinalFormFieldNoCol
    name={inputNamePrefix ? `${inputNamePrefix}.${inputName}` : inputName}
    label="Zip Code"
    component={ThemeInput}
    componentProps={{ mask: zipCodeMask }}
    validate={composeValidators(validateZipCode, validateRequired)}
    {...rest}
  />
);
